import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Activity } from 'lucide-react';
import { useT } from '../i18n';
import { TEAM_AVATARS, TEAM_THEMES } from './teamAvatars';
import { AgentMetric } from '../lib/operatingMetrics';

/* =================================================================
   OperatingIntelligence — "Meet the Intelligence Behind Kitxens"

   This section answers a different question than every other scroll:
   "Who is behind the intelligence?"

   It is intentionally NOT a team page, NOT a testimonial wall, NOT a
   directory of employees. It is built as a calm, premium operating
   center: six AI specialists rendered as active nodes with a live
   pulse, a role, a "Now" responsibility line, and space reserved for
   future live metrics. The aesthetic intent: Mission Control / Stripe
   Atlas / Linear Insights — confident, precise, slightly future.

   Visual language: deep neutral background, restrained motion
   (pulsing status dot + subtle hover lift), single brand accent.
   Every node is human-led; the bottom strip makes that explicit.
   ================================================================= */

const AGENT_THEMES = TEAM_THEMES;

// Rotating "NOW" tasks per agent (order matches TEAM_AVATARS) — the section
// should feel like a live operations floor, not a static directory.
const NOW_TASKS = {
  en: [
    ['Handling guest conversations', 'Answering a WhatsApp booking', 'Replying to a new Google review'],
    ['Prospecting corporate catering accounts', 'Qualifying a catering lead', 'Booking a discovery call'],
    ['Coordinating workflows', 'Compiling the weekly KPI report', 'Syncing supplier follow-ups'],
    ['Scheduling campaigns', 'Publishing to Google Business', 'Formatting the newsletter'],
    ['Publishing visibility content', 'Researching AI search trends', 'Optimizing an article for ChatGPT'],
    ['Preparing operational documents', 'Reviewing a supplier agreement', 'Flagging a lease clause'],
  ],
  es: [
    ['Atendiendo conversaciones de clientes', 'Respondiendo una reserva por WhatsApp', 'Contestando una reseña nueva en Google'],
    ['Prospectando cuentas de catering corporativo', 'Calificando un lead de catering', 'Agendando una discovery call'],
    ['Coordinando flujos de trabajo', 'Compilando el reporte semanal de KPIs', 'Sincronizando seguimientos con proveedores'],
    ['Programando campañas', 'Publicando en Google Business', 'Dando formato al newsletter'],
    ['Publicando contenido de visibilidad', 'Investigando tendencias de búsqueda IA', 'Optimizando un artículo para ChatGPT'],
    ['Preparando documentos operativos', 'Revisando un contrato de proveedor', 'Marcando una cláusula de arrendamiento'],
  ],
};

/* Fades between an agent's live tasks — staggered per agent so the grid
   never changes all at once. */
function NowTask({ tasks, offset = 0 }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % tasks.length), 5200 + offset * 850);
    return () => clearInterval(t);
  }, [tasks.length, offset]);
  return (
    <AnimatePresence mode="wait">
      <motion.p
        key={i}
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -5 }}
        transition={{ duration: 0.35 }}
        className="text-neutral-200 text-[14px] leading-relaxed"
      >
        {tasks[i]}
      </motion.p>
    </AnimatePresence>
  );
}

// Hex equivalents of the AGENT_THEMES dot colors — used by the
// sparkline stroke so it visually matches each agent's accent.
const AGENT_HEX = [
  '#F77F11', // Rachel — brand orange
  '#38bdf8', // Stan   — sky-400
  '#34d399', // Eva    — emerald-400
  '#fbbf24', // Sonny  — amber-400
  '#a78bfa', // Penny  — violet-400
  '#fb7185', // Linda  — rose-400
];

function PulseDot({ tone }) {
  return (
    <span className="relative inline-flex w-2 h-2 shrink-0">
      <span
        className={`absolute inset-0 rounded-full ${tone} opacity-60`}
        style={{ animation: 'kx-pulse 2.4s ease-out infinite' }}
      />
      <span className={`relative inline-flex rounded-full w-2 h-2 ${tone}`} />
    </span>
  );
}

export default function OperatingIntelligence() {
  const { t, lang } = useT();
  const agents = t('operatingIntelligence.agents') || [];

  return (
    <section
      className="relative bg-[#0B0D12] text-white py-24 lg:py-32 overflow-hidden"
      id="operating-intelligence"
      data-testid="operating-intelligence"
    >
      {/* Local keyframes for the status pulse — confined to this section */}
      <style>{`
        @keyframes kx-pulse {
          0%   { transform: scale(1);   opacity: 0.55; }
          70%  { transform: scale(2.6); opacity: 0;    }
          100% { transform: scale(2.6); opacity: 0;    }
        }
      `}</style>

      {/* Soft top glow + subtle grid for "operating center" feel */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 70% 50% at 50% 0%, rgba(247,127,17,0.14), transparent 60%)',
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage:
            'linear-gradient(to right, rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,1) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage:
            'radial-gradient(ellipse 80% 60% at 50% 40%, black 40%, transparent 80%)',
        }}
      />

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 mb-5">
            <Activity className="w-3.5 h-3.5 text-[#F77F11]" />
            <p
              className="kx-eyebrow-dark"
              data-testid="oi-eyebrow"
            >
              {t('operatingIntelligence.eyebrow')}
            </p>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-semibold tracking-[-0.02em] leading-[1.1]">
            {t('operatingIntelligence.titleA')}
            <br />
            <span className="bg-gradient-to-r from-[#F77F11] to-[#ff9b4a] bg-clip-text text-transparent pb-[0.12em] -mb-[0.12em]">
              {t('operatingIntelligence.titleB')}
            </span>
          </h2>
          <p
            className="mt-5 text-neutral-300 text-base sm:text-[17px] leading-relaxed max-w-2xl"
            data-testid="oi-body"
          >
            {t('operatingIntelligence.body')}
          </p>
        </motion.div>

        {/* Agent grid — 6 nodes */}
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {agents.map((a, i) => {
            const theme = AGENT_THEMES[i] || AGENT_THEMES[0];
            const avatar = TEAM_AVATARS[i];
            const meetCta = `${t('operatingIntelligence.meet')} ${a.name}`;
            return (
              <motion.div
                key={a.name}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                whileHover={{ y: -3 }}
                data-testid={`oi-agent-${i}`}
              >
                <Link
                  to={`/operating-intelligence#${avatar?.key || ''}`}
                  aria-label={meetCta}
                  className="group relative h-full rounded-2xl border border-white/10 bg-white/[0.025] hover:bg-white/[0.04] hover:border-white/20 backdrop-blur-sm transition-all duration-300 p-5 sm:p-6 flex flex-col cursor-pointer"
                  data-testid={`oi-agent-link-${i}`}
                >
                  {/* Top row — avatar + status */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <motion.span
                        animate={{ scale: [1, 1.045, 1] }}
                        transition={{ duration: 3.6, repeat: Infinity, ease: 'easeInOut', delay: i * 0.5 }}
                        className={`relative inline-flex w-12 h-12 rounded-xl bg-gradient-to-br ${theme.ring} items-center justify-center overflow-hidden ${theme.halo} shrink-0`}
                      >
                        <img
                          src={avatar?.img}
                          alt={a.name}
                          loading="lazy"
                          className="w-full h-full object-cover"
                          data-testid={`oi-agent-img-${i}`}
                        />
                      </motion.span>
                      <div className="min-w-0">
                        <p
                          className="text-white text-[17px] font-semibold tracking-[-0.01em] leading-tight group-hover:text-[#ff9b4a] transition-colors"
                          data-testid={`oi-agent-name-${i}`}
                        >
                          {a.name}
                        </p>
                        <p className="mt-0.5 text-neutral-400 text-[12px] tracking-[0.14em] uppercase font-semibold">
                          {a.role}
                        </p>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2 py-0.5 shrink-0">
                      <PulseDot tone="bg-emerald-400" />
                      <span className="text-[9.5px] tracking-[0.18em] uppercase font-bold text-emerald-300">
                        {t('operatingIntelligence.onlineLabel')}
                      </span>
                    </span>
                  </div>

                  {/* "Now" line — rotating live responsibility */}
                  <div className="mt-6 pt-5 border-t border-white/[0.06] min-h-[74px]">
                    <p className="text-[9.5px] tracking-[0.22em] uppercase font-bold text-neutral-500 mb-1.5">
                      {t('operatingIntelligence.nowLabel')}
                    </p>
                    <NowTask
                      tasks={(NOW_TASKS[lang === 'es' ? 'es' : 'en'][i]) || [a.now]}
                      offset={i}
                    />
                  </div>

                  {/* Live-metric row — deterministic-by-day count + ticking */}
                  {/* "updated Xs ago" timestamp + per-agent sparkline.        */}
                  <div className="mt-5 flex items-center gap-2 flex-1 min-w-0">
                    <span className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/[0.03] px-1.5 py-0.5 text-[10px] tracking-[0.18em] uppercase font-bold text-neutral-400 shrink-0">
                      <PulseDot tone={theme.dot} />
                      {t('operatingIntelligence.liveLabel')}
                    </span>
                    <AgentMetric
                      agentKey={avatar?.key || a.name.toLowerCase()}
                      isEs={lang === 'es'}
                      color={AGENT_HEX[i] || '#F77F11'}
                      variant="compact"
                      className="flex-1 min-w-0"
                    />
                  </div>

                  {/* Bottom CTA — deep-link affordance, always visible (mobile-first) */}
                  <div className="mt-5 pt-4 border-t border-white/[0.06] flex items-center justify-between gap-3">
                    <span className="text-[12.5px] font-semibold text-neutral-300 group-hover:text-white transition-colors truncate">
                      {meetCta}
                    </span>
                    <span className="inline-flex w-7 h-7 rounded-full border border-white/15 bg-white/[0.04] group-hover:bg-[#F77F11] group-hover:border-[#F77F11] items-center justify-center transition-all shrink-0">
                      <ArrowUpRight className="w-3.5 h-3.5 text-[#F77F11] group-hover:text-white transition-colors" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom strip — operating summary + CTA */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.45 }}
          className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-5"
          data-testid="oi-summary-strip"
        >
          <div className="flex items-center gap-3 min-w-0">
            <PulseDot tone="bg-emerald-400" />
            <p className="text-[14.5px] sm:text-[15px] text-neutral-200 font-medium leading-snug">
              {t('operatingIntelligence.summary')}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 shrink-0">
            <Link
              to="/operating-intelligence"
              className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#F77F11] hover:text-white bg-white/[0.04] hover:bg-[#F77F11] border border-white/10 hover:border-[#F77F11] rounded-full px-4 py-2 transition-colors"
              data-testid="oi-cta"
            >
              {t('operatingIntelligence.cta')}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-white bg-[#F77F11] hover:bg-[#d8651a] rounded-full px-4 py-2 transition-colors"
              data-testid="oi-pricing-cta"
            >
              {lang === 'es' ? 'Ponlos a trabajar — desde $99/mes' : 'Put them to work — from $99/mo'}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
